import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { trpcClient } from "@/lib/trpc";
import {
  ExternalLink,
  Layers,
  AlertCircle,
  ArrowRight,
} from "lucide-react";
import { fmtShort } from "./Charts";
import { getStatusConfig } from "@/config/status-registry";
import { ZIndexView, useZIndexView } from "@/components/shared/ZIndexView";

export interface DrillDownConfig {
  open: boolean;
  type: string;
  title: string;
  period?: string;
  status?: string;
}

interface DashboardDrillDownDrawerProps {
  config: DrillDownConfig;
  onClose: () => void;
}

export function DashboardDrillDownDrawer({
  config,
  onClose,
}: DashboardDrillDownDrawerProps) {
  const navigate = useNavigate();
  const zIndex = useZIndexView();

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard-drilldown", config.type, config.period, config.status],
    queryFn: () =>
      trpcClient.dashboard.drillDown.query({
        type: config.type,
        period: config.period,
        status: config.status,
        limit: 15,
      }),
    enabled: config.open && !!config.type,
    staleTime: 15_000,
  });

  const items = data?.items || [];
  const title = data?.title || config.title || "Chi tiết dữ liệu";
  const viewAllUrl = data?.viewAllUrl || "/";

  const handleNavigateViewAll = () => {
    onClose();
    if (viewAllUrl) {
      navigate({ to: viewAllUrl as any });
    }
  };

  const handleRowClick = (item: any) => {
    // Chống kích hoạt khi bôi đen text copy
    if (window.getSelection()?.toString().trim().length) return;

    if (item.linkUrl) {
      onClose();
      navigate({ to: item.linkUrl as any });
      return;
    }
    if (config.type === "quotations_overdue") {
      zIndex.openView("quotation", item.id);
    } else if (config.type === "delayed_pos") {
      zIndex.openView("productionOrder", item.id);
    } else if (config.type === "stopped_machines") {
      onClose();
      navigate({ to: `/factory/machine-logs` as any });
    } else if (config.type === "urgent_deliveries") {
      zIndex.openView("delivery", item.id);
    } else if (config.type === "overdue_debtors") {
      zIndex.openView("customer", item.customerId || item.id);
    } else if (config.type === "orders_by_status") {
      zIndex.openView("order", item.id);
    } else if (config.type === "paper_surplus") {
      if (item.poId) {
        zIndex.openView("productionOrder", item.poId);
      } else {
        onClose();
        navigate({ to: `/planning/paper-allocation` as any });
      }
    } else {
      onClose();
      navigate({ to: viewAllUrl as any });
    }
  };

  return (
    <>
    <Dialog open={config.open} onOpenChange={(val) => !val && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0 overflow-hidden bg-card border-border shadow-2xl">
        {/* Drawer Header (Tối Giản) */}
        <DialogHeader className="shrink-0">
          <div className="flex items-center justify-between pr-6">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 bg-primary/10 text-primary rounded-lg">
                <Layers className="h-4 w-4" />
              </div>
              <div>
                <DialogTitle>
                  {title}
                </DialogTitle>
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-0.5">
                  <span className="font-semibold text-primary">
                    {items.length} bản ghi
                  </span>
                  <span>•</span>
                  <span>Kỳ: {config.period || "Hiện tại"}</span>
                </div>
              </div>
            </div>
          </div>
          <DialogDescription className="sr-only">
            Danh sách chi tiết bản ghi phân tích từ bảng điều khiển điều hành
          </DialogDescription>
        </DialogHeader>

        {/* Content Table Area */}
        <div className="flex-1 overflow-y-auto p-3.5 min-h-[260px]">
          {isLoading ? (
            <div className="space-y-2.5">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-2.5 border border-border rounded-lg bg-card"
                >
                  <div className="space-y-1.5 flex-1">
                    <Skeleton className="h-3.5 w-28" />
                    <Skeleton className="h-3 w-44" />
                  </div>
                  <Skeleton className="h-5 w-16" />
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
              <div className="p-2.5 bg-muted rounded-full mb-2">
                <AlertCircle className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-xs font-semibold text-foreground">
                Không có bản ghi nào
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Các chỉ số hiện tại đang ở mức an toàn hoặc đạt chuẩn vận hành.
              </p>
            </div>
          ) : (
            <div className="border border-border rounded-lg overflow-x-auto bg-card shadow-xs">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/50 border-b border-border text-muted-foreground uppercase text-[10px]">
                    <th className="text-left px-3 py-2 font-semibold">Mã</th>
                    <th className="text-left px-3 py-2 font-semibold">Đối tượng / Chi tiết</th>
                    <th className="text-right px-3 py-2 font-semibold">Giá trị / SL</th>
                    <th className="text-center px-3 py-2 font-semibold">Trạng thái</th>
                    <th className="text-right px-3 py-2 font-semibold">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((item: any) => {
                    const st = getStatusConfig("OrderStatus", item.status);
                    return (
                      <tr
                        key={item.id}
                        onClick={() => handleRowClick(item)}
                        className="hover:bg-muted/40 transition-colors cursor-pointer group"
                      >
                        <td className="px-3 py-2 font-mono font-bold text-primary">
                          {item.code}
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-foreground break-words whitespace-normal max-w-[220px]">
                            {item.title}
                          </div>
                          {item.meta && (
                            <div className="text-[10px] text-muted-foreground break-words whitespace-normal max-w-[260px]">
                              {item.meta}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-foreground">
                          {item.amount > 0
                            ? item.amount >= 1000
                              ? fmtShort(item.amount)
                              : item.amount.toLocaleString("vi-VN")
                            : "—"}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Badge
                            variant="outline"
                            className="text-[10px] font-medium border px-1.5 py-0 rounded-full"
                          >
                            {st.label || item.status}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-primary group-hover:bg-primary/10 gap-1 text-[11px]"
                          >
                            Xem <ArrowRight className="h-3 w-3" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Drawer Footer */}
        <div className="p-2.5 border-t border-border bg-muted/20 flex items-center justify-between shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="text-xs h-7"
          >
            Đóng
          </Button>
          <Button
            size="sm"
            onClick={handleNavigateViewAll}
            className="text-xs h-7 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold gap-1.5"
          >
            Xem toàn bộ
            <ExternalLink className="h-3 w-3" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    <ZIndexView
      open={zIndex.open}
      onOpenChange={zIndex.onOpenChange}
      type={zIndex.type}
      id={zIndex.id}
    />
    </>
  );
}
